/**
 * App-root provider that owns the single `assistant-ui` runtime instance for
 * the in-platform chat (PLT-4625).
 *
 * Why this lives at the app root rather than per-route:
 *
 * Today's chat has two entry-points and we want the conversation to survive
 * transitions between them within a single browser session:
 *
 *   1. The full `/chat` route — used as a top-level destination.
 *   2. The right-side `ChatSidePanel` — opened in-place from things like
 *      Net Impact dimension bars and (eventually) other contextual entry
 *      points.
 *
 * Both consumers render `<UprightChat>` (or a subset of it) which is now a
 * pure consumer of the runtime via assistant-ui's `useAssistantRuntime`
 * hooks. Mounting the runtime once at the app root means:
 *
 *   - The thread state, in-flight stream, attached files, and composer state
 *     all persist when the user clicks the "Expand to full chat" button on
 *     the side panel — the panel unmounts, but the runtime keeps running
 *     under the still-mounted provider, so the `/chat` route renders the
 *     same thread the user was just looking at.
 *   - We don't have to invent a serialization / rehydration path between two
 *     parallel runtime instances (which assistant-ui's `useLocalRuntime`
 *     does not expose a clean API for).
 *
 * Performance: `AssistantRuntimeProvider` only publishes the runtime via
 * React context. Components that don't call `useThread` / `useMessage` /
 * `useAssistantRuntime` don't re-render on chat updates. So mounting it
 * everywhere is cheap when the chat isn't visible.
 *
 * Constraint: this must be mounted inside `<BrowserRouter>` because some
 * tool UIs use `useNavigate` indirectly. It is mounted in `MainRoot` rather
 * than `AppRoot` to keep it out of the `RenderApiProfile` short-circuit
 * path (which renders a screenshot-only view that has no use for chat).
 */
import { ReactNode, useEffect } from 'react';
import { AssistantRuntimeProvider, useLocalRuntime } from '@assistant-ui/react';
import { ResourcelessPermissionId } from '@upright/common/iam/privilege/permission/types';
import { useHasResourcelessPrivilege } from '@upright/ui/api/iam/privilege/query-options';
import { uprightChatAdapter } from './upright-chat-adapter';
import { setAutoReattachEnabled } from './reattach-attachments';
import { uprightAttachmentAdapter } from './upright-chat-shared';

interface Props {
  children: ReactNode;
}

const DISABLE_CHAT_STORAGE_KEY = 'upright:disable-chat';

/**
 * Dev/debug escape hatch (NOT a product feature): skip mounting the chat
 * runtime when the URL contains `?disableChat=1` or localStorage
 * `upright:disable-chat` is '1'. Toggle off with `?disableChat=0`.
 *
 * The URL flag is mirrored into localStorage so it survives in-app client-side
 * navigation within the session. Intended for screenshot/automation tooling
 * that needs a page to mount without the assistant-ui runtime.
 *
 * While active, chat surfaces (the right-side panel and the `/chat` route) will
 * NOT work — their `useAssistantRuntime` consumers have no provider — so only
 * use this when capturing non-chat views.
 */
function isChatRuntimeDisabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const param = new URLSearchParams(window.location.search).get('disableChat');
    if (param !== null) {
      const on = param !== '0' && param !== 'false';
      window.localStorage.setItem(DISABLE_CHAT_STORAGE_KEY, on ? '1' : '0');
      return on;
    }
    return window.localStorage.getItem(DISABLE_CHAT_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function ChatRuntimeProvider({ children }: Props) {
  // Read once at mount; the flag is not expected to change without a reload.
  if (isChatRuntimeDisabled()) {
    return <>{children}</>;
  }
  return <ChatRuntimeProviderWithRuntime>{children}</ChatRuntimeProviderWithRuntime>;
}

function ChatRuntimeProviderWithRuntime({ children }: Props) {
  const runtime = useLocalRuntime(uprightChatAdapter, {
    adapters: {
      attachments: uprightAttachmentAdapter,
    },
  });

  // PLT-5607: the chat adapter runs outside the React tree, so publish the
  // fund-upload entitlement into module state it can read. This gates the
  // small-file auto-re-attach (PLT-5558) to fund-upload users; everyone else
  // keeps the pre-PLT-5558 behaviour. Team users short-circuit to `true` in the
  // hook, matching the backend gate. Defaults to `false` until resolved.
  const { hasPrivilege: canUseFundUpload } = useHasResourcelessPrivilege(ResourcelessPermissionId.USE_FUND_UPLOAD);
  useEffect(() => {
    setAutoReattachEnabled(canUseFundUpload);
  }, [canUseFundUpload]);

  return <AssistantRuntimeProvider runtime={runtime}>{children}</AssistantRuntimeProvider>;
}
